/**
 * UploadProgress — shows upload and analysis progress for a video.
 * Listens to Socket.io events: video:progress, video:complete, video:failed.
 */
import React, { useEffect, useState } from 'react';
import { StatusBadge } from '../../ui/Badge.js';

const STEPS = [
  { key: 'uploading', label: 'Uploading', threshold: 0 },
  { key: 'queued', label: 'Queued', threshold: 100 },
  { key: 'processing', label: 'Analyzing pose', threshold: 100 },
  { key: 'saving', label: 'Saving results', threshold: 70 },
  { key: 'complete', label: 'Complete', threshold: 100 },
];

export function UploadProgress({ videoId, uploadPct, socket, onComplete }) {
  const [analysisPct, setAnalysisPct] = useState(0);
  const [status, setStatus] = useState('uploading');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!socket || !videoId) return;

    const onProgress = ({ videoId: vid, pct, status: s }) => {
      if (Number(vid) !== Number(videoId)) return;
      setAnalysisPct(pct);
      setStatus(s || 'processing');
    };

    const onComplete = ({ videoId: vid }) => {
      if (Number(vid) !== Number(videoId)) return;
      setStatus('completed');
      setAnalysisPct(100);
      onComplete?.(videoId);
    };

    const onFailed = ({ videoId: vid, error: e }) => {
      if (Number(vid) !== Number(videoId)) return;
      setStatus('failed');
      setError(e || 'Analysis failed');
    };

    socket.on('video:progress', onProgress);
    socket.on('video:complete', onComplete);
    socket.on('video:failed', onFailed);

    return () => {
      socket.off('video:progress', onProgress);
      socket.off('video:complete', onComplete);
      socket.off('video:failed', onFailed);
    };
  }, [socket, videoId, onComplete]);

  // Overall progress: upload is first half, analysis is second half
  const overallPct = uploadPct < 100
    ? Math.floor(uploadPct / 2)
    : 50 + Math.floor(analysisPct / 2);

  const isDone = status === 'completed';
  const isFailed = status === 'failed';

  return (
    <div className="bg-gray-50 rounded-xl p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          {isDone ? 'Analysis complete' : isFailed ? 'Analysis failed' : 'Processing video…'}
        </span>
        <StatusBadge status={isDone ? 'completed' : isFailed ? 'failed' : 'processing'} />
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={[
            'h-full rounded-full transition-all duration-500',
            isFailed ? 'bg-danger' : isDone ? 'bg-accent' : 'bg-primary',
          ].join(' ')}
          style={{ width: `${overallPct}%` }}
        />
      </div>

      <div className="flex justify-between text-xs text-gray-400">
        <span>{uploadPct < 100 ? `Uploading ${uploadPct}%` : 'Upload done'}</span>
        <span>{overallPct}%</span>
      </div>

      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}

export default UploadProgress;
